import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { Admin } from "../lib/api";

// ════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════

interface LoginAttempt {
  _id: string;
  username?: string | null;
  ip: string;
  userAgent?: string | null;
  success: boolean;
  failureReason?: string | null;
  timestamp: string;
  location?: {
    country?: string;
    city?: string;
  } | null;
}

interface FailedLogin {
  _id: string;
  username?: string | null;
  ip: string;
  reason: string; // "wrong_password" | "unknown_account" | "rate_limit" | "account_locked"
  attemptCount: number;
  timestamp: string;
  userAgent?: string | null;
}

interface BlockedIp {
  _id: string;
  ip: string;
  reason: string;
  blockDuration: number; // minutes
  attemptCount: number;
  blockedAt: string;
  expiresAt?: string | null;
  isActive: boolean;
}

interface LoginStats {
  totalAttemptsToday: number;
  failedAttemptsToday: number;
  blockedIps: number;
  failedGrowthRate: number; // Percentage change compared to yesterday
}

type TabType = "attempts" | "failures" | "blocks";
type TimeFilter = "24h" | "7d";
type SuccessFilter = "all" | "success" | "failed";

// ════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════

export default function AdminLoginAttempts() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("attempts");

  // Stats
  const [stats, setStats] = useState<LoginStats>({
    totalAttemptsToday: 0,
    failedAttemptsToday: 0,
    blockedIps: 0,
    failedGrowthRate: 0,
  });

  // Data
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [failures, setFailures] = useState<FailedLogin[]>([]);
  const [blocks, setBlocks] = useState<BlockedIp[]>([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("24h");
  const [successFilter, setSuccessFilter] = useState<SuccessFilter>("all");
  const [selectedItem, setSelectedItem] = useState<LoginAttempt | FailedLogin | BlockedIp | null>(null);

  // Unblock operation
  const [unblockBusy, setUnblockBusy] = useState(false);
  const [unblockError, setUnblockError] = useState<string | null>(null);

  // Check admin permission
  if (!user?.isAdmin) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h1>🚫 Erişim Engellendi</h1>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Load Functions
  // ─────────────────────────────────────────────────────────

  async function loadStats() {
    try {
      // Mock stats calculation from loaded data
      // In real scenario, backend would provide dedicated stats endpoint
      const totalAttempts = attempts.length;
      const failedAttempts = attempts.filter((a) => !a.success).length;
      const activeBlocks = blocks.filter((b) => b.isActive).length;

      setStats({
        totalAttemptsToday: totalAttempts,
        failedAttemptsToday: failedAttempts,
        blockedIps: activeBlocks,
        failedGrowthRate: 12.5, // Mock growth rate
      });
    } catch (e: any) {
      console.error("Stats calculation failed:", e);
    }
  }

  async function loadAttempts() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.getLoginAttempts({ limit: 200 });
      if (response?.ok && response?.attempts) {
        setAttempts(response.attempts);
      } else if (Array.isArray(response)) {
        setAttempts(response);
      } else {
        // Mock data for development
        setAttempts([
          {
            _id: "1",
            username: "johndoe",
            ip: "192.168.1.100",
            userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64)",
            success: true,
            timestamp: new Date().toISOString(),
          },
          {
            _id: "2",
            username: "janedoe",
            ip: "192.168.1.101",
            userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)",
            success: false,
            failureReason: "wrong_password",
            timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
          },
          {
            _id: "3",
            username: "attacker",
            ip: "10.0.0.99",
            userAgent: "Python/3.9 requests/2.26.0",
            success: false,
            failureReason: "rate_limit",
            timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          },
        ]);
      }
    } catch (e: any) {
      console.error("Login attempts load failed:", e);
      setError(e?.message || "Login denemeleri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadFailures() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.getFailedLogins({ limit: 200 });
      if (response?.ok && response?.failures) {
        setFailures(response.failures);
      } else if (Array.isArray(response)) {
        setFailures(response);
      } else {
        // Mock data for development
        setFailures([
          {
            _id: "1",
            username: "janedoe",
            ip: "192.168.1.101",
            reason: "wrong_password",
            attemptCount: 3,
            timestamp: new Date().toISOString(),
            userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)",
          },
          {
            _id: "2",
            username: null,
            ip: "10.0.0.99",
            reason: "unknown_account",
            attemptCount: 15,
            timestamp: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
            userAgent: "Python/3.9 requests/2.26.0",
          },
          {
            _id: "3",
            username: "admin",
            ip: "203.0.113.42",
            reason: "rate_limit",
            attemptCount: 50,
            timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
            userAgent: "curl/7.68.0",
          },
        ]);
      }
    } catch (e: any) {
      console.error("Failed logins load failed:", e);
      setError(e?.message || "Başarısız girişler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadBlocks() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.getBlockedIps({ limit: 100 });
      if (response?.ok && response?.blocks) {
        setBlocks(response.blocks);
      } else if (Array.isArray(response)) {
        setBlocks(response);
      } else {
        // Mock data for development
        setBlocks([
          {
            _id: "1",
            ip: "10.0.0.99",
            reason: "Excessive failed login attempts",
            blockDuration: 60,
            attemptCount: 25,
            blockedAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
            expiresAt: new Date(Date.now() + 1000 * 60 * 45).toISOString(),
            isActive: true,
          },
          {
            _id: "2",
            ip: "203.0.113.42",
            reason: "Brute force attack detected",
            blockDuration: 1440,
            attemptCount: 150,
            blockedAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
            expiresAt: new Date(Date.now() + 1000 * 60 * 1320).toISOString(),
            isActive: true,
          },
        ]);
      }
    } catch (e: any) {
      console.error("Blocked IPs load failed:", e);
      setError(e?.message || "Engellenmiş IP'ler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadData() {
    switch (activeTab) {
      case "attempts":
        await loadAttempts();
        break;
      case "failures":
        await loadFailures();
        break;
      case "blocks":
        await loadBlocks();
        break;
    }
    await loadStats();
  }

  async function handleUnblock(ip: string) {
    if (!confirm(`${ip} adresinin engelini kaldırmak istediğinizden emin misiniz?`)) {
      return;
    }

    setUnblockBusy(true);
    setUnblockError(null);
    try {
      await Admin.unblockIp(ip);
      alert(`✅ ${ip} engelinden kaldırıldı.`);
      await loadBlocks();
      setSelectedItem(null);
    } catch (e: any) {
      console.error("Unblock failed:", e);
      setUnblockError(e?.message || "Engel kaldırma başarısız oldu.");
      alert(`❌ Hata: ${e?.message || "Engel kaldırılamadı."}`);
    } finally {
      setUnblockBusy(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ─────────────────────────────────────────────────────────
  // Filter Data
  // ─────────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    let result: any[] = [];
    switch (activeTab) {
      case "attempts":
        result = attempts;
        break;
      case "failures":
        result = failures;
        break;
      case "blocks":
        result = blocks;
        break;
    }

    // Time filter (24h or 7d)
    if (timeFilter === "24h") {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      result = result.filter((item) => {
        const timestamp = "timestamp" in item ? item.timestamp : "blockedAt" in item ? item.blockedAt : null;
        if (!timestamp) return true;
        return new Date(timestamp).getTime() >= cutoff;
      });
    } else if (timeFilter === "7d") {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      result = result.filter((item) => {
        const timestamp = "timestamp" in item ? item.timestamp : "blockedAt" in item ? item.blockedAt : null;
        if (!timestamp) return true;
        return new Date(timestamp).getTime() >= cutoff;
      });
    }

    // Success filter (only for attempts tab)
    if (activeTab === "attempts" && successFilter !== "all") {
      result = result.filter((item) => {
        if (successFilter === "success") return (item as LoginAttempt).success === true;
        if (successFilter === "failed") return (item as LoginAttempt).success === false;
        return true;
      });
    }

    // Search query
    if (query) {
      result = result.filter((item) => {
        const usernameMatch = item.username?.toLowerCase().includes(query);
        const ipMatch = item.ip?.toLowerCase().includes(query);
        const userAgentMatch = item.userAgent?.toLowerCase().includes(query);
        const reasonMatch = "reason" in item ? item.reason?.toLowerCase().includes(query) : false;
        const failureReasonMatch = "failureReason" in item ? item.failureReason?.toLowerCase().includes(query) : false;
        return usernameMatch || ipMatch || userAgentMatch || reasonMatch || failureReasonMatch;
      });
    }

    return result;
  }, [activeTab, attempts, failures, blocks, searchQuery, timeFilter, successFilter]);

  // ─────────────────────────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────────────────────────

  function formatDate(dateStr?: string | null) {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("tr-TR");
    } catch {
      return dateStr;
    }
  }

  function timeAgo(dateStr?: string | null) {
    if (!dateStr) return "";
    try {
      const now = Date.now();
      const then = new Date(dateStr).getTime();
      const diffMs = now - then;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Az önce";
      if (diffMins < 60) return `${diffMins} dk önce`;
      if (diffHours < 24) return `${diffHours} saat önce`;
      return `${diffDays} gün önce`;
    } catch {
      return "";
    }
  }

  function getReasonBadge(reason: string) {
    const styles: Record<string, { bg: string; color: string; border: string; label: string }> = {
      wrong_password: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.4)", label: "Yanlış Şifre" },
      unknown_account: { bg: "rgba(251, 191, 36, 0.1)", color: "#fbbf24", border: "1px solid rgba(251, 191, 36, 0.4)", label: "Bilinmeyen Hesap" },
      rate_limit: { bg: "rgba(249, 115, 22, 0.1)", color: "#f97316", border: "1px solid rgba(249, 115, 22, 0.4)", label: "Rate Limit" },
      account_locked: { bg: "rgba(168, 85, 247, 0.1)", color: "#a855f7", border: "1px solid rgba(168, 85, 247, 0.4)", label: "Hesap Kilitli" },
    };
    const style = styles[reason] || { bg: "rgba(156, 163, 175, 0.1)", color: "#9ca3af", border: "1px solid rgba(156, 163, 175, 0.4)", label: reason };
    return (
      <span
        style={{
          ...style,
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {style.label}
      </span>
    );
  }

  // Loading state
  if (loading && filteredData.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <p style={{ color: "var(--muted)" }}>Login verileri yükleniyor...</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "var(--text)" }}>🔐 Login Attempts / Failed Logins</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => loadData()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(59, 130, 246, 0.4)",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              color: "#3b82f6",
              cursor: "pointer",
            }}
          >
            <span>🔄</span>
            <span>Yenile</span>
          </button>
          <button
            onClick={() => navigate("/settings")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(156, 163, 175, 0.4)",
              backgroundColor: "rgba(156, 163, 175, 0.1)",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            <span>←</span>
            <span>Geri</span>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            borderRadius: 10,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: "#ef4444" }}>Veri yüklenemedi</div>
              <div style={{ fontSize: 14, color: "#ef4444", opacity: 0.9 }}>{error}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => loadData()}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(239, 68, 68, 0.4)",
                backgroundColor: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              🔄 Yeniden Dene
            </button>
            <button
              onClick={() => setError(null)}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(156, 163, 175, 0.4)",
                backgroundColor: "rgba(156, 163, 175, 0.1)",
                color: "#9ca3af",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Unblock error banner */}
      {unblockError && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            borderRadius: 10,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>❌</span>
            <div>
              <div style={{ fontWeight: 700, color: "#ef4444" }}>Engel kaldırma hatası</div>
              <div style={{ fontSize: 14, color: "#ef4444", opacity: 0.9 }}>{unblockError}</div>
            </div>
          </div>
          <button
            onClick={() => setUnblockError(null)}
            style={{
              padding: "6px 12px",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              border: "1px solid rgba(156, 163, 175, 0.4)",
              backgroundColor: "rgba(156, 163, 175, 0.1)",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(34, 197, 94, 0.4)",
            backgroundColor: "rgba(34, 197, 94, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#22c55e", marginBottom: 4 }}>{stats.totalAttemptsToday}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#22c55e", opacity: 0.8 }}>Toplam Login (Bugün)</div>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(239, 68, 68, 0.4)",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#ef4444", marginBottom: 4 }}>{stats.failedAttemptsToday}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#ef4444", opacity: 0.8 }}>Başarısız Login (Bugün)</div>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(249, 115, 22, 0.4)",
            backgroundColor: "rgba(249, 115, 22, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#f97316", marginBottom: 4 }}>{stats.blockedIps}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f97316", opacity: 0.8 }}>Engellenen IP</div>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(168, 85, 247, 0.4)",
            backgroundColor: "rgba(168, 85, 247, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#a855f7", marginBottom: 4 }}>
            {stats.failedGrowthRate > 0 ? "+" : ""}
            {stats.failedGrowthRate.toFixed(1)}%
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#a855f7", opacity: 0.8 }}>Büyüme Oranı (Dün)</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", padding: "4px 0" }}>
        {[
          { key: "attempts", label: "📊 Login Attempts" },
          { key: "failures", label: "❌ Failed Logins" },
          { key: "blocks", label: "🚫 IP Blocks" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key as TabType);
              setSearchQuery("");
              setSelectedItem(null);
            }}
            style={{
              flex: "1 1 auto",
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 700,
              borderRadius: 8,
              border: activeTab === tab.key ? "1px solid rgba(59, 130, 246, 0.5)" : "1px solid rgba(156, 163, 175, 0.3)",
              backgroundColor: activeTab === tab.key ? "rgba(59, 130, 246, 0.15)" : "var(--bg2)",
              color: activeTab === tab.key ? "#3b82f6" : "var(--text)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters Row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {/* Search */}
        <input
          type="text"
          placeholder="🔍 Ara (kullanıcı, IP, user agent...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: "1 1 300px",
            padding: "10px 12px",
            fontSize: 14,
            borderRadius: 8,
            border: "1px solid rgba(156, 163, 175, 0.3)",
            backgroundColor: "var(--bg2)",
            color: "var(--text)",
          }}
        />

        {/* Time Filter */}
        <select
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
          style={{
            padding: "10px 12px",
            fontSize: 14,
            borderRadius: 8,
            border: "1px solid rgba(156, 163, 175, 0.3)",
            backgroundColor: "var(--bg2)",
            color: "var(--text)",
            cursor: "pointer",
          }}
        >
          <option value="24h">Son 24 Saat</option>
          <option value="7d">Son 7 Gün</option>
        </select>

        {/* Success Filter (only for attempts tab) */}
        {activeTab === "attempts" && (
          <select
            value={successFilter}
            onChange={(e) => setSuccessFilter(e.target.value as SuccessFilter)}
            style={{
              padding: "10px 12px",
              fontSize: 14,
              borderRadius: 8,
              border: "1px solid rgba(156, 163, 175, 0.3)",
              backgroundColor: "var(--bg2)",
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            <option value="all">Tümü</option>
            <option value="success">✅ Başarılı</option>
            <option value="failed">❌ Başarısız</option>
          </select>
        )}
      </div>

      {/* Main Content Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 1.2fr)", gap: 16 }}>
        {/* Left: Table */}
        <div>
          <div
            style={{
              overflowX: "auto",
              borderRadius: 10,
              border: "1px solid rgba(156, 163, 175, 0.2)",
              backgroundColor: "var(--bg2)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ backgroundColor: "rgba(156, 163, 175, 0.08)" }}>
                  {activeTab === "attempts" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Kullanıcı</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>IP</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>User Agent</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Başarılı?</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Zaman</th>
                    </>
                  )}
                  {activeTab === "failures" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Kullanıcı</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>IP</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Hata Sebebi</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İstek Sayısı</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Zaman</th>
                    </>
                  )}
                  {activeTab === "blocks" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>IP</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Sebep</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Block Süresi</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İstek</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İşlem</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
                      {searchQuery ? "Arama sonucu bulunamadı" : "Henüz kayıt yok"}
                    </td>
                  </tr>
                )}
                {activeTab === "attempts" &&
                  (filteredData as LoginAttempt[]).map((attempt) => (
                    <tr
                      key={attempt._id}
                      onClick={() => setSelectedItem(attempt)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === attempt._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== attempt._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== attempt._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontWeight: 600 }}>{attempt.username || "-"}</td>
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12 }}>{attempt.ip}</td>
                      <td
                        style={{
                          padding: 12,
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          color: "var(--muted)",
                        }}
                      >
                        {attempt.userAgent || "-"}
                      </td>
                      <td style={{ padding: 12 }}>
                        {attempt.success ? (
                          <span style={{ color: "#22c55e", fontSize: 12, fontWeight: 700 }}>✅ Yes</span>
                        ) : (
                          <span style={{ color: "#ef4444", fontSize: 12, fontWeight: 700 }}>❌ No</span>
                        )}
                      </td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>
                        <div>{formatDate(attempt.timestamp)}</div>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{timeAgo(attempt.timestamp)}</div>
                      </td>
                    </tr>
                  ))}
                {activeTab === "failures" &&
                  (filteredData as FailedLogin[]).map((failure) => (
                    <tr
                      key={failure._id}
                      onClick={() => setSelectedItem(failure)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === failure._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== failure._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== failure._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontWeight: 600 }}>{failure.username || "-"}</td>
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12 }}>{failure.ip}</td>
                      <td style={{ padding: 12 }}>{getReasonBadge(failure.reason)}</td>
                      <td style={{ padding: 12, color: "#ef4444", fontWeight: 700 }}>{failure.attemptCount}</td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>
                        <div>{formatDate(failure.timestamp)}</div>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{timeAgo(failure.timestamp)}</div>
                      </td>
                    </tr>
                  ))}
                {activeTab === "blocks" &&
                  (filteredData as BlockedIp[]).map((block) => (
                    <tr
                      key={block._id}
                      onClick={() => setSelectedItem(block)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === block._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== block._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== block._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>{block.ip}</td>
                      <td
                        style={{
                          padding: 12,
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                        }}
                      >
                        {block.reason}
                      </td>
                      <td style={{ padding: 12, color: "#f97316", fontWeight: 600 }}>{block.blockDuration} dk</td>
                      <td style={{ padding: 12, color: "#ef4444", fontWeight: 700 }}>{block.attemptCount}</td>
                      <td style={{ padding: 12 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnblock(block.ip);
                          }}
                          disabled={unblockBusy || !block.isActive}
                          style={{
                            padding: "6px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            border: block.isActive ? "1px solid rgba(34, 197, 94, 0.4)" : "1px solid rgba(156, 163, 175, 0.3)",
                            backgroundColor: block.isActive ? "rgba(34, 197, 94, 0.1)" : "rgba(156, 163, 175, 0.1)",
                            color: block.isActive ? "#22c55e" : "#9ca3af",
                            cursor: block.isActive ? "pointer" : "not-allowed",
                            opacity: unblockBusy ? 0.6 : 1,
                          }}
                        >
                          {unblockBusy ? "⏳" : "🔓"} Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div
          style={{
            borderRadius: 10,
            border: "1px solid rgba(156, 163, 175, 0.2)",
            backgroundColor: "var(--bg2)",
            padding: 20,
            height: "fit-content",
            position: "sticky",
            top: 24,
          }}
        >
          {!selectedItem ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Sol taraftan bir kayıt seç</div>
              <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>Detaylı bilgiler burada görünecek</div>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(156, 163, 175, 0.2)" }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 8 }}>
                  {activeTab === "attempts" && "📊 Login Attempt Detayı"}
                  {activeTab === "failures" && "❌ Failed Login Detayı"}
                  {activeTab === "blocks" && "🚫 IP Block Detayı"}
                </h2>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--muted)", wordBreak: "break-all" }}>
                  {selectedItem._id}
                </div>
              </div>

              {/* Dynamic Content */}
              <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
                {/* Common Fields */}
                {"ip" in selectedItem && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>IP:</span>
                    <span style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700 }}>{selectedItem.ip}</span>
                  </div>
                )}

                {"username" in selectedItem && selectedItem.username && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Kullanıcı:</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedItem.username}</span>
                  </div>
                )}

                {/* Attempt-specific */}
                {activeTab === "attempts" && "success" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Durum:</span>
                      {selectedItem.success ? (
                        <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 13 }}>✅ Başarılı</span>
                      ) : (
                        <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 13 }}>❌ Başarısız</span>
                      )}
                    </div>
                    {"failureReason" in selectedItem && selectedItem.failureReason && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Sebep:</span>
                        <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>{selectedItem.failureReason}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Failure-specific */}
                {activeTab === "failures" && "reason" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Hata Sebebi:</span>
                      {getReasonBadge(selectedItem.reason)}
                    </div>
                    {"attemptCount" in selectedItem && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Deneme Sayısı:</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>{selectedItem.attemptCount}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Block-specific */}
                {activeTab === "blocks" && "reason" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Sebep:</span>
                      <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>{selectedItem.reason}</span>
                    </div>
                    {"blockDuration" in selectedItem && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Block Süresi:</span>
                        <span style={{ fontSize: 13, color: "#f97316", fontWeight: 700 }}>{selectedItem.blockDuration} dk</span>
                      </div>
                    )}
                    {"attemptCount" in selectedItem && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Deneme Sayısı:</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>{selectedItem.attemptCount}</span>
                      </div>
                    )}
                    {"isActive" in selectedItem && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Aktif:</span>
                        {selectedItem.isActive ? (
                          <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 13 }}>🔴 Evet</span>
                        ) : (
                          <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 13 }}>✅ Hayır</span>
                        )}
                      </div>
                    )}
                    {"blockedAt" in selectedItem && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Engellenme:</span>
                        <span style={{ fontSize: 12 }}>{formatDate(selectedItem.blockedAt)}</span>
                      </div>
                    )}
                    {"expiresAt" in selectedItem && selectedItem.expiresAt && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>Bitiş:</span>
                        <span style={{ fontSize: 12 }}>{formatDate(selectedItem.expiresAt)}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Timestamp */}
                {"timestamp" in selectedItem && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Zaman:</span>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12 }}>{formatDate(selectedItem.timestamp)}</div>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>{timeAgo(selectedItem.timestamp)}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* User Agent Section */}
              {"userAgent" in selectedItem && selectedItem.userAgent && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🌐 User Agent</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 11,
                      fontFamily: "monospace",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedItem.userAgent}
                  </div>
                </div>
              )}

              {/* Location Section */}
              {"location" in selectedItem && selectedItem.location && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📍 Lokasyon</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 13,
                    }}
                  >
                    {selectedItem.location.city && <div>Şehir: {selectedItem.location.city}</div>}
                    {selectedItem.location.country && <div>Ülke: {selectedItem.location.country}</div>}
                  </div>
                </div>
              )}

              {/* Unblock Button (for blocks tab) */}
              {activeTab === "blocks" && "isActive" in selectedItem && selectedItem.isActive && (
                <button
                  onClick={() => handleUnblock(selectedItem.ip)}
                  disabled={unblockBusy}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    marginBottom: 12,
                    fontSize: 14,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: "1px solid rgba(34, 197, 94, 0.4)",
                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                    color: "#22c55e",
                    cursor: unblockBusy ? "not-allowed" : "pointer",
                    opacity: unblockBusy ? 0.6 : 1,
                  }}
                >
                  {unblockBusy ? "⏳ İşleniyor..." : "🔓 Unblock IP"}
                </button>
              )}

              {/* Close Button */}
              <button
                onClick={() => setSelectedItem(null)}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: 8,
                  border: "1px solid rgba(156, 163, 175, 0.4)",
                  backgroundColor: "rgba(156, 163, 175, 0.1)",
                  color: "#9ca3af",
                  cursor: "pointer",
                }}
              >
                Kapat
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
